import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchWithToken } from '../services/api';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];

const UploadProfileImage = () => {
  const query = useQuery();
  const email = query.get('email') || '';
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [step, setStep] = useState('photo'); // 'photo' or 'location'
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [locationSuccess, setLocationSuccess] = useState(false); // Add new state

  // Kullanıcı id, username ve rolünü localStorage'dan al
  let id = null;
  let username = '';
  let role = '';
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    id = user?.id;
    username = user?.username || '';
    role = user?.role || '';
  } catch {}

  // Şehirleri çek
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch('https://turkiyeapi.dev/api/v1/provinces');
        const result = await response.json();
        if (result.status === "OK" && Array.isArray(result.data)) {
          setCities(result.data);
        }
      } catch {}
    };
    fetchCities();
  }, []);

  // İlçe çek
  useEffect(() => {
    if (!selectedCityId) {
      setDistricts([]);
      setSelectedDistrict('');
      return;
    }
    const fetchDistricts = async () => {
      try {
        const response = await fetch(`https://turkiyeapi.dev/api/v1/provinces/${selectedCityId}`);
        const result = await response.json();
        if (result.status === "OK" && result.data) {
          setDistricts(result.data.districts || []);
        }
      } catch {}
    };
    fetchDistricts();
  }, [selectedCityId]);

  // localStorage'daki user'ın city/district bilgisini oku ve seçili olarak göster
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.city) {
        const cityObj = cities.find(c => c.name === user.city);
        if (cityObj) {
          setSelectedCityId(cityObj.id);
          setSelectedCityName(cityObj.name);
        }
      }
      if (user?.district) {
        setSelectedDistrict(user.district);
      }
    } catch {}
    // eslint-disable-next-line
  }, [cities.length]);

  const handleFileChange = (e) => {
    setError('');
    const selected = e.target.files[0];
    if (selected) {
      if (selected.size > 20 * 1024 * 1024) { // 20MB limit
        setFile(null);
        setPreview(null);
        setError('Dosya boyutu 20MB\'dan küçük olmalıdır.');
        return;
      }
      if (allowedTypes.includes(selected.type)) {
        setFile(selected);
        setPreview(URL.createObjectURL(selected));
      } else {
        setFile(null);
        setPreview(null);
        setError('Sadece resim dosyaları yükleyebilirsiniz (jpg, jpeg, png, gif, webp).');
      }
    }
  };

  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    setError('');
    if (!file) {
      setError('Lütfen bir resim seçin.');
      return;
    }
    if (!id) {
      setError('Kullanıcı bulunamadı.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('userId', id);

      const response = await fetchWithToken('/v1/user/upload/profileImage', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Yükleme başarısız.');
      }

      let profileImageUrl = null;
      try {
        const data = await response.json();
        profileImageUrl = data?.data || data?.profileImageUrl || null;
      } catch {}

      // localStorage güncelleme
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (profileImageUrl) {
          user.profileImageUrl = profileImageUrl;
          localStorage.setItem('user', JSON.stringify(user));
          window.dispatchEvent(new Event('user-updated'));
          setUploadedImageUrl(profileImageUrl);
        }
      } catch {}

      setSuccess(true);

      // Kullanıcı rolünü kontrol et
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user?.role === 'ROLE_TEACHER') {
        // Öğretmen ise konum seçme ekranına geç
        setStep('location');
      } else {
        // Öğrenci ise ana sayfaya yönlendir
        setTimeout(() => navigate('/'), 1500);
      }
    } catch (err) {
      setError('Yükleme başarısız. Lütfen tekrar deneyin.');
    } finally {
      setUploading(false);
    }
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!selectedCityName || !selectedDistrict) {
      setError('Lütfen şehir ve ilçe seçiniz.');
      return;
    }

    setUploading(true);
    try {
      const response = await fetchWithToken('/v1/teacher/location/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: id,
          city: selectedCityName,
          district: selectedDistrict
        })
      });

      if (!response.ok) {
        throw new Error('Konum güncellenirken bir hata oluştu');
      }

      setLocationSuccess(true);
      setTimeout(() => {
        if (role === 'ROLE_TEACHER') {
          navigate('/teacher-dashboard');
        } else {
          navigate('/');
        }
      }, 1500);
    } catch (err) {
      setError('Konum güncellenirken hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setUploading(false);
    }
  };

  // Öğrenciler için atla butonu, öğretmen için uyarı ve ikinci basışta geçiş
  const [skipError, setSkipError] = useState('');
  const [teacherSkipCount, setTeacherSkipCount] = useState(0);

  const handleSkip = () => {
    setError('');
    setSkipError('');
    if (role === 'ROLE_TEACHER') {
      if (teacherSkipCount === 0) {
        setSkipError('Profil fotoğrafınızın olması öğrencilerin sizi bulmasını ve güven duymasını kolaylaştırır. Lütfen bir fotoğraf yükleyin. Yine de devam etmek istiyorsanız tekrar "Atla"ya basın.');
        setTeacherSkipCount(1);
      } else {
        setStep('location');
      }
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 sm:p-8 flex flex-col items-center">
        {step === 'photo' ? (
          // Fotoğraf yükleme ekranı
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              Profil Fotoğrafı Yükle
            </h2>
            <p className="text-gray-500 dark:text-gray-300 text-sm mb-2 text-center">
              Profil fotoğrafınız sizi daha görünür kılar. Lütfen net bir yüz fotoğrafı yükleyin.
            </p>
            {username && (
              <div className="mb-4 text-center">
                <span className="inline-block bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full text-sm font-medium">
                  @{username}
                </span>
              </div>
            )}
            <form onSubmit={handlePhotoUpload} className="space-y-4 w-full flex flex-col items-center">
              <div className="flex flex-col items-center w-full mb-4">
                <div className="relative mb-4">
                  <div className="w-32 h-32 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden border-4 border-blue-200 dark:border-blue-700 shadow">
                    {preview ? (
                      <img
                        src={preview}
                        alt="Profil önizleme"
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <svg className="w-16 h-16 text-gray-300 dark:text-gray-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 15.232a4 4 0 01-6.464 0M12 18v.01M12 6a4 4 0 110 8 4 4 0 010-8z" />
                      </svg>
                    )}
                  </div>
                  <label
                    htmlFor="profile-upload"
                    className="absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow cursor-pointer transition-colors"
                    title="Fotoğraf seç"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <input
                      id="profile-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploading || success}
                      className="hidden"
                    />
                  </label>
                </div>
                <span className="text-xs text-gray-400 dark:text-gray-500">Desteklenen: jpg, jpeg, png, gif, webp</span>
              </div>
              {error && <div className="text-red-500 text-sm text-center">{error}</div>}
              {skipError && <div className="text-orange-500 text-sm text-center">{skipError}</div>}
              {success && <div className="text-green-600 text-sm text-center">Yükleme başarılı! Ana sayfaya yönlendiriliyorsunuz...</div>}
              <button
                type="submit"
                disabled={uploading || !file || success}
                className={`w-full py-3 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow ${
                  (uploading || !file || success) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Yükleniyor...' : 'Yükle'}
              </button>
              {/* Atla butonu sadece fotoğraf yükleme adımında ve sadece öğrenci için aktif */}
              <button
                type="button"
                onClick={handleSkip}
                disabled={uploading || success}
                className={`w-full py-3 rounded-xl font-medium mt-2 ${
                  role === 'ROLE_TEACHER'
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                } transition-colors shadow`}
              >
                Atla
              </button>
            </form>
          </>
        ) : (
          // Konum seçme ekranı
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
              Konum Bilgisi
            </h2>
            <p className="text-gray-500 dark:text-gray-300 text-sm mb-4 text-center">
              Öğrencilerinizin sizi bulabilmesi için konum bilgilerinizi ekleyin.
            </p>
            {uploadedImageUrl && (
              <div className="mb-6">
                <img
                  src={uploadedImageUrl}
                  alt="Profil"
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg mx-auto"
                />
              </div>
            )}
            <form onSubmit={handleLocationSubmit} className="w-full space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Şehir
                  </label>
                  <select
                    value={selectedCityId}
                    onChange={e => {
                      const cityId = parseInt(e.target.value);
                      const cityObj = cities.find(c => c.id === cityId);
                      setSelectedCityId(cityId);
                      setSelectedCityName(cityObj ? cityObj.name : '');
                      setSelectedDistrict('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 dark:bg-gray-700"
                  >
                    <option value="">Şehir Seçiniz</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>{city.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    İlçe
                  </label>
                  <select
                    value={selectedDistrict}
                    onChange={e => setSelectedDistrict(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500/30 dark:bg-gray-700"
                    disabled={!selectedCityId}
                  >
                    <option value="">İlçe Seçiniz</option>
                    {districts.map(district => (
                      <option key={district.id} value={district.name}>{district.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              {error && <div className="text-red-500 text-sm text-center">{error}</div>}
              {locationSuccess && <div className="text-green-600 text-sm text-center">Konum kaydedildi! Yönlendiriliyorsunuz...</div>}
              <button
                type="submit"
                disabled={uploading}
                className={`w-full py-3 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors shadow ${
                  uploading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {uploading ? 'Kaydediliyor...' : 'Kaydet ve Devam Et'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadProfileImage;
